// src/pages/operations-dashboard/components/AttemptAnalyticsSection.jsx
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  Cell,
} from "recharts";
import { RotateCcw, Package, Truck, Hash, TrendingDown } from "lucide-react";
import {
  formatCompact,
  formatShortDate,
  CHART_COLORS,
} from "../../../utils/dashboardConstants";

const AttemptTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl px-4 py-3">
      <p className="text-xs font-semibold text-gray-500 mb-2">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4 py-0.5">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-xs text-gray-600">{entry.name}</span>
          </div>
          <span className="text-xs font-semibold text-gray-800">{formatCompact(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const DistributionTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white/95 backdrop-blur-lg border border-gray-100 rounded-xl shadow-2xl px-4 py-3">
      <p className="text-sm font-semibold text-gray-800 mb-1">{d?.attempt_label || `Attempt ${d?.attempt_number}`}</p>
      <p className="text-lg font-bold text-gray-900">{formatCompact(d?.count || d?.value)}</p>
      {d?.percentage !== undefined && <p className="text-xs text-gray-400">{d.percentage.toFixed(1)}% of total</p>}
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, subtitle, bgColor, textColor }) => (
  <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:shadow-lg hover:shadow-gray-100/50 transition-all">
    <div className={`p-3 rounded-xl ${bgColor}`}>
      <Icon className={`w-5 h-5 ${textColor}`} strokeWidth={2} />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900 tracking-tight">{value}</p>
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const Skeleton = ({ className = "" }) => (
  <div className={`bg-gray-100 rounded-lg animate-pulse ${className}`} />
);

export default function AttemptAnalyticsSection({ data, loading }) {
  const isLoading = loading?.attemptAnalytics;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Prepare distribution data
  const pickupDist = (data.pickup_attempt_distribution || []).map((d) => ({
    attempt_label: d.attempt_label || `Attempt ${d.attempt_number}`,
    attempt_number: d.attempt_number,
    count: d.count || 0,
    percentage: d.percentage || 0,
  }));

  const deliveryDist = (data.delivery_attempt_distribution || []).map((d) => ({
    attempt_label: d.attempt_label || `Attempt ${d.attempt_number}`,
    attempt_number: d.attempt_number,
    count: d.count || 0,
    percentage: d.percentage || 0,
  }));

  // Daily trends
  const dailyTrends = (data.daily_attempt_trends || []).map((d) => ({
    date: d.date,
    "Pickup Attempts": d.pickup_attempts || 0,
    "Delivery Attempts": d.delivery_attempts || 0,
  }));

  const avgPickup = data.average_pickup_attempts_per_order || data.avg_pickup_attempts_per_order || 0;
  const avgDelivery = data.average_delivery_attempts_per_order || data.avg_delivery_attempts_per_order || 0;

  return (
    <div className="space-y-4">
      {/* Summary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={Hash}
          label="Total Orders"
          value={formatCompact(data.total_orders || 0)}
          bgColor="bg-blue-50"
          textColor="text-blue-500"
        />
        <MetricCard
          icon={Package}
          label="Pickup Attempts"
          value={formatCompact(data.total_pickup_attempts || 0)}
          subtitle={`Avg: ${avgPickup.toFixed(2)} per order`}
          bgColor="bg-violet-50"
          textColor="text-violet-500"
        />
        <MetricCard
          icon={Truck}
          label="Delivery Attempts"
          value={formatCompact(data.total_delivery_attempts || 0)}
          subtitle={`Avg: ${avgDelivery.toFixed(2)} per order`}
          bgColor="bg-emerald-50"
          textColor="text-emerald-500"
        />
        <MetricCard
          icon={TrendingDown}
          label="First-Attempt Success"
          value={
            pickupDist.length > 0 && pickupDist[0]?.percentage
              ? `${pickupDist[0].percentage.toFixed(1)}%`
              : "–"
          }
          subtitle="Pickup 1st attempt rate"
          bgColor="bg-amber-50"
          textColor="text-amber-500"
        />
      </div>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pickup Distribution */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-violet-50">
                <Package className="w-4.5 h-4.5 text-violet-500" strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Pickup Attempt Distribution</h3>
                <p className="text-xs text-gray-400 mt-0.5">How many attempts before successful pickup</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {pickupDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={pickupDist} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="attempt_label"
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatCompact}
                  />
                  <Tooltip content={<DistributionTooltip />} cursor={{ fill: "#f8fafc", radius: 6 }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={50}>
                    {pickupDist.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No pickup data</div>
            )}
          </div>
        </div>

        {/* Delivery Distribution */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-emerald-50">
                <Truck className="w-4.5 h-4.5 text-emerald-500" strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Delivery Attempt Distribution</h3>
                <p className="text-xs text-gray-400 mt-0.5">Attempts before successful delivery</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            {deliveryDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={deliveryDist} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="attempt_label"
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatCompact}
                  />
                  <Tooltip content={<DistributionTooltip />} cursor={{ fill: "#f8fafc", radius: 6 }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={50}>
                    {deliveryDist.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[(i + 4) % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No delivery data</div>
            )}
          </div>
        </div>
      </div>

      {/* Daily Trends */}
      {dailyTrends.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-indigo-50">
                <RotateCcw className="w-4.5 h-4.5 text-indigo-500" strokeWidth={2} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Daily Attempt Trend</h3>
                <p className="text-xs text-gray-400 mt-0.5">Pickup & delivery attempts over time</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={dailyTrends} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatShortDate}
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatCompact}
                />
                <Tooltip content={<AttemptTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
                <Line
                  type="monotone"
                  dataKey="Pickup Attempts"
                  stroke="#8b5cf6"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4, fill: "#8b5cf6", stroke: "white", strokeWidth: 2 }}
                />
                <Line
                  type="monotone"
                  dataKey="Delivery Attempts"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4, fill: "#10b981", stroke: "white", strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
